import { Box, Card, Stack, Text } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { useState, useMemo } from 'react';

import { CHATBOT_INTERACTION_TYPES } from 'routes/SearchPerson/SearchFilter/constants';

import { ConfirmationChip } from './components/ConfirmationChip';
import { FilterSummaryMessage } from './components/FilterSummaryMessage';
import { PlainTextMessage } from './components/PlainTextMessage';
import { SuggestionsMessage } from './components/SuggestionsMessage';
import { SearchpalMessageBlock, FilterData } from './types';
import {
  ROLES,
  MESSAGES_SECTION_TYPES,
  CONFIRM_CHIP_VARIANTS,
} from './utils/constants';

export interface SearchpalChatMessageProps {
  blocks: SearchpalMessageBlock[];
  role: (typeof ROLES)[keyof typeof ROLES];
  timestamp: string;
  // eslint-disable-next-line no-unused-vars
  setChatbotOpen: (type: string) => void;
  chatbotIcon?: string;
  expandContent?: boolean;
  // eslint-disable-next-line no-unused-vars
  onConfirmFilters?: (messageId: string) => void;
  shouldShowConfirmChip?: boolean;
  shouldShowViewResultsChip?: boolean;
}

export function SearchpalChatMessage({
  blocks,
  setChatbotOpen,
  chatbotIcon,
  role,
  timestamp,
  expandContent,
  onConfirmFilters,
  shouldShowConfirmChip,
  shouldShowViewResultsChip,
}: SearchpalChatMessageProps) {
  const [chipConfirmed, setChipConfirmed] = useState(false);

  const messageId = useMemo(() => `${timestamp}`, [timestamp]);
  const { isDesktop } = useScreenSize();
  const handleConfirm = () => {
    setChipConfirmed(true);
    if (onConfirmFilters) {
      onConfirmFilters(messageId);
    }
  };

  const handleViewResults = () => {
    setChatbotOpen(CHATBOT_INTERACTION_TYPES.CLOSE);
  };

  const renderers = {
    [MESSAGES_SECTION_TYPES.plain]: (
      block: SearchpalMessageBlock,
      idx: number
    ) => {
      const { text } = block as {
        type: typeof MESSAGES_SECTION_TYPES.plain;
        text: string;
      };
      return <PlainTextMessage key={idx} text={text} role={role} />;
    },
    [MESSAGES_SECTION_TYPES.filters]: (
      block: SearchpalMessageBlock,
      idx: number
    ) => {
      const { filters } = block as {
        type: typeof MESSAGES_SECTION_TYPES.filters;
        filters: FilterData;
      };
      return <FilterSummaryMessage key={idx} filters={filters} />;
    },
    [MESSAGES_SECTION_TYPES.suggestions]: (
      block: SearchpalMessageBlock,
      idx: number
    ) => {
      const { suggestions } = block as {
        type: typeof MESSAGES_SECTION_TYPES.suggestions;
        suggestions: string[];
      };
      return <SuggestionsMessage key={idx} suggestions={suggestions} />;
    },
  };

  const content = (
    <>
      {blocks.map((block, idx) =>
        renderers[block.type] ? renderers[block.type](block, idx) : null
      )}
    </>
  );

  return (
    <Box
      alignSelf={role === ROLES.assistant ? 'flex-start' : 'flex-end'}
      data-testid="chat-message"
      maxWidth={expandContent ? '100%' : '70%'}
    >
      <Stack direction="row" alignItems="flex-start">
        {role === ROLES.assistant && chatbotIcon && (
          <img
            src={chatbotIcon}
            alt="searchpal"
            style={{ width: '32px', height: '32px', marginRight: '10px' }}
          />
        )}
        <Stack direction="column">
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-end"
            marginBottom={0.5}
          >
            <Text color="text.primary" variant="caption" fontSize="12px">
              {role === ROLES.assistant ? 'Search Assistant' : 'Me'}
            </Text>
            <Text color="text.secondary" variant="caption" fontSize="11px">
              {timestamp}
            </Text>
          </Stack>
          <Card
            sx={(theme) => ({
              width: 'fit-content',
              backgroundColor:
                role === ROLES.assistant
                  ? theme.palette.background.paper
                  : theme.palette.primary.main,
              alignSelf: role === ROLES.assistant ? 'flex-start' : 'flex-end',
              boxShadow: 'none',
              borderRadius:
                role === ROLES.assistant
                  ? '16px 16px 16px 4px'
                  : '16px 16px 4px 16px',
              padding: theme.spacing(2),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            })}
          >
            {content}
          </Card>

          {shouldShowConfirmChip && (
            <ConfirmationChip
              onConfirm={handleConfirm}
              disabled={chipConfirmed}
              variant={CONFIRM_CHIP_VARIANTS.confirm}
            />
          )}
          {shouldShowViewResultsChip && !isDesktop && (
            <ConfirmationChip
              onConfirm={handleViewResults}
              variant={CONFIRM_CHIP_VARIANTS.viewResults}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
